import { IconText } from "@codexteam/icons";

/**
 * Build styles
 */
require("./index.css").toString();

/**
 * @typedef {object} ParagraphConfig
 * @property {string} placeholder - placeholder for the empty paragraph
 * @property {boolean} preserveBlank - Whether or not to keep blank paragraphs when saving editor data
 */

/**
 * @typedef {Object} ParagraphData
 * @description Tool's input and output data format
 * @property {String} text — Paragraph's content. Can include HTML tags: <a><b><i>
 */
export default class Body {
  static get DEFAULT_PLACEHOLDER() {
    return `Let's create an awesome slide!`;
  }

  constructor({ data, config, api, readOnly }) {
    this.config = config;
    this.api = api;
    this.readOnly = readOnly;

    this._CSS = {
      block: this.api.styles.block,
      wrapper: "ce-paragraph",
      container: "ce-paragraph-container",
    };

    this._placeholder = config.placeholder
      ? config.placeholder
      : Body.DEFAULT_PLACEHOLDER;

    this._data = {};
    this._preserveBlank =
      config.preserveBlank !== undefined ? config.preserveBlank : false;
    this._defaultContent = config.defaultContent ?? null;

    this._element = this.drawView();
    this.data = data;
  }

  /**
   * Creates a single contenteditable paragraph element
   * @param {string} cssClass
   * @param {string} placeholder
   * @returns {HTMLElement}
   * @private
   */
  _createInput(cssClass, placeholder) {
    const el = document.createElement("P");

    el.classList.add(cssClass, this._CSS.block);
    el.contentEditable = this.readOnly ? "false" : "true";
    el.dataset.placeholder = this.api.i18n.t(placeholder);


    if(this.config.muiClassName){
      el.classList.add(this.config.muiClassName);
    }
    return el;
  }

  drawView() {
    // Always create the main text input
    this._textElement = this._createInput(this._CSS.wrapper, this._placeholder);

    const container = document.createElement("DIV");
    container.classList.add(this._CSS.container);

    container.appendChild(this._textElement);

    return container;
  }

  render() {
    return this._element;
  }

  merge(data) {
    this.data = {
      text: this.data.text + data.text,
    };
  }

  validate(savedData) {
    if (savedData.text.trim() === "" && !this._preserveBlank) {
      return false;
    }
    return true;
  }

  save(toolsContent) {
    const saved = {
      text: this._textElement.innerHTML,
    };

    return saved;
  }

  onPaste(event) {
    this.data = {
      text: event.detail.data.innerHTML,
    };
  }

  static get sanitize() {
    return {
      text: {
        br: true,
        b: true,
        strong: true,
        i: true,
        em: true,
        u: true,
        a: true,
      },
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  static get isLineBreaksEnabled() {
    return true;
  }

  get data() {
    this._data.text = this._textElement.innerHTML;

    return this._data;
  }

  set data(data) {
    this._data = data || {};

    this._textElement.innerHTML =
      this._data.text || (this._defaultContent ?? "");
  }

  static get pasteConfig() {
    return {
      tags: ["P"],
    };
  }

  static get toolbox() {
    return {
      icon: IconText,
      title: "Body text",
    };
  }
}
